import { UserRole } from '../services/AuthContext';

// הגדרת הרשאות לכל תפקיד
export interface Permissions {
  canViewCustomers: boolean;
  canEditCustomers: boolean;
  canDeleteCustomers: boolean;
  canViewStores: boolean;
  canEditStores: boolean;
  canDeleteStores: boolean;
  canViewDevices: boolean;
  canEditDevices: boolean;
  canDeleteDevices: boolean;
  canViewSpareParts: boolean;
  canEditSpareParts: boolean;
  canDeleteSpareParts: boolean;
  canViewServiceCalls: boolean;
  canEditServiceCalls: boolean;
  canDeleteServiceCalls: boolean;
  canViewEmployees: boolean;
  canEditEmployees: boolean;
  canDeleteEmployees: boolean;
  canViewDashboard: boolean;
  canExportData: boolean;
  canImportData: boolean;
  canViewKnowledgeBase: boolean;
  canEditKnowledgeBase: boolean;
  canDeleteKnowledgeBase: boolean;
  canCommentKnowledgeBase: boolean;
  canSendMessages: boolean;
  canViewMessages: boolean;
}

// הרשאות ברירת מחדל (צופה - רק צפייה)
const defaultPermissions: Permissions = {
  canViewCustomers: true,
  canEditCustomers: false,
  canDeleteCustomers: false,
  canViewStores: true,
  canEditStores: false,
  canDeleteStores: false,
  canViewDevices: true,
  canEditDevices: false,
  canDeleteDevices: false,
  canViewSpareParts: true,
  canEditSpareParts: false,
  canDeleteSpareParts: false,
  canViewServiceCalls: true,
  canEditServiceCalls: false,
  canDeleteServiceCalls: false,
  canViewEmployees: false,
  canEditEmployees: false,
  canDeleteEmployees: false,
  canViewDashboard: true,
  canExportData: false,
  canImportData: false,
  canViewKnowledgeBase: true,
  canEditKnowledgeBase: false,
  canDeleteKnowledgeBase: false,
  canCommentKnowledgeBase: true,
  canSendMessages: true,
  canViewMessages: true,
};

// הרשאות מנהל - הכל מותר
const adminPermissions: Permissions = {
  canViewCustomers: true,
  canEditCustomers: true,
  canDeleteCustomers: true,
  canViewStores: true,
  canEditStores: true,
  canDeleteStores: true,
  canViewDevices: true,
  canEditDevices: true,
  canDeleteDevices: true,
  canViewSpareParts: true,
  canEditSpareParts: true,
  canDeleteSpareParts: true,
  canViewServiceCalls: true,
  canEditServiceCalls: true,
  canDeleteServiceCalls: true,
  canViewEmployees: true,
  canEditEmployees: true,
  canDeleteEmployees: true,
  canViewDashboard: true,
  canExportData: true,
  canImportData: true,
  canViewKnowledgeBase: true,
  canEditKnowledgeBase: true,
  canDeleteKnowledgeBase: true,
  canCommentKnowledgeBase: true,
  canSendMessages: true,
  canViewMessages: true,
};

// הרשאות שירות - לקוחות, חנויות, קריאות שירות (ללא מכשירים)
const servicePermissions: Permissions = {
  ...defaultPermissions,
  canViewCustomers: true,
  canEditCustomers: true,
  canDeleteCustomers: true,
  canViewStores: true,
  canEditStores: true,
  canDeleteStores: true,
  canViewDevices: false, // שירות לא רואה מכשירים
  canEditDevices: false,
  canDeleteDevices: false,
  canViewSpareParts: false, // שירות לא רואה חלקי חילוף
  canEditSpareParts: false,
  canDeleteSpareParts: false,
  canViewServiceCalls: true,
  canEditServiceCalls: true,
  canExportData: false,
  canImportData: false,
};

// הרשאות טכנאי - קריאות שירות
const technicianPermissions: Permissions = {
  ...defaultPermissions,
  canEditServiceCalls: true,
  canViewSpareParts: true,
};

// הרשאות סוכן שטח - לקוחות וקריאות שירות
const fieldAgentPermissions: Permissions = {
  ...defaultPermissions,
  canEditCustomers: true,
  canViewServiceCalls: true,
  canEditServiceCalls: true,
  canViewStores: true,
  canExportData: false,
  canImportData: false,
};

// הרשאות צופה חיצוני - רק צפייה בקריאות שירות של החנות שלו
const externalViewerPermissions: Permissions = {
  ...defaultPermissions,
  canViewCustomers: false,
  canViewStores: false,
  canViewDevices: false,
  canViewSpareParts: false,
  canViewEmployees: false,
  canViewServiceCalls: true,
  canEditServiceCalls: false,
  canDeleteServiceCalls: false,
  canExportData: true,
  canImportData: false,
  canViewDashboard: true,
  canViewKnowledgeBase: false,
  canEditKnowledgeBase: false,
  canDeleteKnowledgeBase: false,
  canCommentKnowledgeBase: false,
  canSendMessages: false,
  canViewMessages: false,
};

// הרשאות מחסן - גישה רק לדשבורד, מאגר ידע והוצאת סחורה מהמלאי
const warehousePermissions: Permissions = {
  ...defaultPermissions,
  canViewCustomers: false,
  canEditCustomers: false,
  canDeleteCustomers: false,
  canViewStores: false,
  canEditStores: false,
  canDeleteStores: false,
  canViewDevices: false,
  canEditDevices: false,
  canDeleteDevices: false,
  canViewSpareParts: false,
  canEditSpareParts: false,
  canDeleteSpareParts: false,
  canViewServiceCalls: false,
  canEditServiceCalls: false,
  canDeleteServiceCalls: false,
  canViewEmployees: false,
  canEditEmployees: false,
  canDeleteEmployees: false,
  canViewDashboard: true,
  canExportData: false,
  canImportData: false,
  canViewKnowledgeBase: true,
  canEditKnowledgeBase: false,
  canDeleteKnowledgeBase: false,
  canCommentKnowledgeBase: true,
  canSendMessages: false,
  canViewMessages: false,
};

// עזב את החברה — אין שום גישה (החשבון גם נעול להתחברות)
const formerEmployeePermissions: Permissions = Object.fromEntries(
  Object.keys(adminPermissions).map((key) => [key, false]),
) as unknown as Permissions;

// פונקציה לקבלת הרשאות לפי תפקיד
export const getPermissions = (role: UserRole | null): Permissions => {
  const enforceSparePartsDeviceDependency = (base: Permissions): Permissions => {
    const canUseSpareParts = base.canViewDevices;
    return {
      ...base,
      canViewSpareParts: base.canViewSpareParts && canUseSpareParts,
      canEditSpareParts: base.canEditSpareParts && canUseSpareParts,
      canDeleteSpareParts: base.canDeleteSpareParts && canUseSpareParts,
    };
  };

  if (!role) {
    return enforceSparePartsDeviceDependency(defaultPermissions);
  }

  switch (role) {
    case 'admin':
      return enforceSparePartsDeviceDependency(adminPermissions);
    case 'service':
      return enforceSparePartsDeviceDependency(servicePermissions);
    case 'technician':
      return enforceSparePartsDeviceDependency(technicianPermissions);
    case 'field_agent':
      return enforceSparePartsDeviceDependency(fieldAgentPermissions);
    case 'external_viewer':
      return enforceSparePartsDeviceDependency(externalViewerPermissions);
    case 'warehouse':
      return enforceSparePartsDeviceDependency(warehousePermissions);
    case 'former_employee':
      return formerEmployeePermissions;
    case 'viewer':
    default:
      return enforceSparePartsDeviceDependency(defaultPermissions);
  }
};

// פונקציות עזר לבדיקת הרשאות
export const hasPermission = (
  role: UserRole | null,
  permission: keyof Permissions
): boolean => {
  const permissions = getPermissions(role);
  return permissions[permission];
};

